import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, XMarkIcon, LinkIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import MathText from '../components/MathText';

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${API_URL}${imagePath}`;
};

// Field status: 'unchanged' | 'modified' | 'saved'
const getBorderClass = (status) => {
  switch (status) {
    case 'modified':
      return 'border-orange-500 dark:border-orange-400';
    case 'saved':
      return 'border-green-500 dark:border-green-400';
    default:
      return 'border-gray-300 dark:border-gray-600';
  }
};

const QuestionLookup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Admin check
  useEffect(() => {
    if (!user?.is_staff && !user?.is_superuser) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter state
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [uncheckedOnly, setUncheckedOnly] = useState(false);
  
  // Questions state
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Edit state
  const [editChapter, setEditChapter] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [editChapters, setEditChapters] = useState([]);
  const [editTopics, setEditTopics] = useState([]);
  const [updating, setUpdating] = useState(false);
  
  // Editable content state
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editAnswers, setEditAnswers] = useState([]);
  const [editExplanation, setEditExplanation] = useState('');
  const [explanationPreviewMode, setExplanationPreviewMode] = useState(false);
  
  // Track field modification status: 'unchanged' | 'modified' | 'saved'
  const [fieldStatus, setFieldStatus] = useState({
    questionText: 'unchanged',
    chapter: 'unchanged',
    topic: 'unchanged',
    explanation: 'unchanged',
    answers: {} // { answerId: 'unchanged' | 'modified' | 'saved' }
  });

  // Concept state
  const [concepts, setConcepts] = useState([]);
  const [questionConcepts, setQuestionConcepts] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState('');
  const [showCreateConcept, setShowCreateConcept] = useState(false);
  const [newConceptIdentifier, setNewConceptIdentifier] = useState('');
  const [newConceptTitle, setNewConceptTitle] = useState('');
  const [newConceptDescription, setNewConceptDescription] = useState('');

  // Create question modal state
  const [showCreateQuestion, setShowCreateQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    subject_id: '',
    chapter_id: '',
    topic_id: '',
    question_number: '',
    question_text: '',
    explanation: '',
    question_type: 'MULTIPLE_CHOICE',
    correct_answer_text: '',
    answers: [
      { answer_text: '', is_correct: true },
      { answer_text: '', is_correct: false },
      { answer_text: '', is_correct: false },
      { answer_text: '', is_correct: false }
    ]
  });

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Load subjects on mount
  useEffect(() => {
    loadSubjects();
  }, []);

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
    } else {
      setChapters([]);
      setSelectedChapter('');
    }
  }, [selectedSubject]);

  // Load topics when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      loadTopics(selectedChapter);
    } else {
      setTopics([]);
      setSelectedTopic('');
    }
  }, [selectedChapter]);

  // Load edit topics when edit chapter changes
  useEffect(() => {
    if (editChapter && editChapters.length > 0) {
      const chapter = editChapters.find(c => c.id === editChapter);
      if (chapter && chapter.topics) {
        setEditTopics(chapter.topics);
      } else {
        setEditTopics([]);
      }
    } else {
      setEditTopics([]);
    }
  }, [editChapter, editChapters]);

  // Load edit chapters and reset edit fields when current question changes
  useEffect(() => {
    const currentQuestion = questions[currentIndex];
    if (currentQuestion && currentQuestion.subject_id) {
      loadEditChapters(currentQuestion.subject_id);
      loadConcepts(currentQuestion.subject_id);
      loadQuestionConcepts(currentQuestion.id);
      setEditChapter(currentQuestion.chapter_id || '');
      setEditTopic(currentQuestion.topic_id || '');
      setEditQuestionText(currentQuestion.question_text || '');
      setEditAnswers(currentQuestion.answers?.map(a => ({ ...a })) || []);
      setEditExplanation(currentQuestion.explanation || '');
      setSelectedImageFile(null);
      // Reset field status
      const answerStatuses = {};
      currentQuestion.answers?.forEach(a => {
        answerStatuses[a.id] = 'unchanged';
      });
      setFieldStatus({
        questionText: 'unchanged',
        chapter: 'unchanged',
        topic: 'unchanged',
        explanation: 'unchanged',
        answers: answerStatuses
      });
    }
  }, [currentIndex, questions]);

  const loadEditChapters = async (subjectId) => {
    try {
      const data = await apiClient.get(`/api/courses/subjects/${subjectId}/structure/`);
      setEditChapters(data?.chapters || []);
    } catch (err) {
      console.error('Failed to load edit chapters:', err);
      setEditChapters([]);
    }
  };

  const loadSubjects = async () => {
    try {
      const data = await apiClient.get('/api/courses/all-subjects/');
      setSubjects(data || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const data = await apiClient.get(`/api/courses/subjects/${subjectId}/structure/`);
      setChapters(data?.chapters || []);
    } catch (err) {
      console.error('Failed to load chapters:', err);
      setChapters([]);
    }
  };

  const loadTopics = async (chapterId) => {
    const chapter = chapters.find(c => c.id === chapterId);
    if (chapter && chapter.topics) {
      setTopics(chapter.topics);
    } else {
      setTopics([]);
    }
  };

  const loadEditTopics = (chapterId) => {
    const chapter = editChapters.find(c => c.id === chapterId);
    if (chapter && chapter.topics) {
      setEditTopics(chapter.topics);
    } else {
      setEditTopics([]);
    }
  };

  // Load concepts for a subject
  const loadConcepts = async (subjectId) => {
    try {
      const data = await apiClient.get(`/api/courses/concepts/subject/${subjectId}/`);
      setConcepts(data || []);
    } catch (err) {
      console.error('Failed to load concepts:', err);
      setConcepts([]);
    }
  };

  // Load concepts assigned to current question
  const loadQuestionConcepts = async (questionId) => {
    try {
      const data = await apiClient.get(`/api/courses/questions/${questionId}/concepts/`);
      setQuestionConcepts(data || []);
    } catch (err) {
      console.error('Failed to load question concepts:', err);
      setQuestionConcepts([]);
    }
  };

  // Create a new concept
  const handleCreateConcept = async () => {
    if (!newConceptIdentifier.trim() || !newConceptTitle.trim()) {
      setError('Identifier and title are required');
      return;
    }

    const currentQuestion = questions[currentIndex];
    if (!currentQuestion?.subject_id) {
      setError('No subject selected');
      return;
    }

    try {
      const data = await apiClient.post('/api/courses/concepts/create/', {
        subject: currentQuestion.subject_id,
        identifier: newConceptIdentifier.trim(),
        title: newConceptTitle.trim(),
        description: newConceptDescription.trim()
      });
      
      setConcepts(prev => [...prev, data]);
      setShowCreateConcept(false);
      setNewConceptIdentifier('');
      setNewConceptTitle('');
      setNewConceptDescription('');
      setSelectedConcept(data.id);
    } catch (err) {
      console.error('Failed to create concept:', err);
      setError(`Failed to create concept: ${err.message}`);
    }
  };

  // Assign question to concept
  const handleAssignConcept = async () => {
    if (!selectedConcept) return;
    
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return;

    try {
      await apiClient.post('/api/courses/concepts/assign/', {
        concept_id: selectedConcept,
        question_id: currentQuestion.id
      });
      
      await loadQuestionConcepts(currentQuestion.id);
      setSelectedConcept('');
    } catch (err) {
      console.error('Failed to assign concept:', err);
      setError(`Failed to assign concept: ${err.message}`);
    }
  };

  // Remove question from concept
  const handleRemoveConcept = async (conceptQuestionId) => {
    try {
      await apiClient.delete(`/api/courses/concepts/remove/${conceptQuestionId}/`);
      setQuestionConcepts(prev => prev.filter(c => c.concept_question_id !== conceptQuestionId));
    } catch (err) {
      console.error('Failed to remove concept:', err);
      setError(`Failed to remove concept: ${err.message}`);
    }
  };

  // Create new question
  const handleCreateQuestion = async () => {
    if (!newQuestion.subject_id || !newQuestion.question_text.trim()) {
      setError('Subject and question text are required');
      return;
    }

    try {
      const data = await apiClient.post('/api/courses/questions/create/', newQuestion);
      setShowCreateQuestion(false);
      setNewQuestion({
        subject_id: '',
        chapter_id: '',
        topic_id: '',
        question_number: '',
        question_text: '',
        explanation: '',
        question_type: 'MULTIPLE_CHOICE',
        correct_answer_text: '',
        answers: [
          { answer_text: '', is_correct: true },
          { answer_text: '', is_correct: false },
          { answer_text: '', is_correct: false },
          { answer_text: '', is_correct: false }
        ]
      });
      // Load the new question
      setQuestions([data]);
      setCurrentIndex(0);
    } catch (err) {
      console.error('Failed to create question:', err);
      setError(`Failed to create question: ${err.message}`);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setQuestions([]);
    setCurrentIndex(0);
    setEditQuestionText('');
    setEditAnswers([]);
    setEditExplanation('');

    try {
      const data = await apiClient.get(`/api/courses/questions/?question_number=${encodeURIComponent(searchQuery.trim())}`);
      const questionsData = Array.isArray(data) ? data : (data?.results || []);
      
      if (questionsData.length > 0) {
        setQuestions(questionsData);
        setCurrentIndex(0);
      } else {
        setError('No questions found with that number.');
      }
    } catch (err) {
      console.error('Question search error:', err);
      setError(`Failed to search for questions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSearch = async () => {
    if (!selectedSubject) {
      setError('Please select a subject.');
      return;
    }

    setLoading(true);
    setError(null);
    setQuestions([]);
    setCurrentIndex(0);
    setEditQuestionText('');
    setEditAnswers([]);
    setEditExplanation('');

    try {
      let url = `/api/courses/questions/?subject=${selectedSubject}`;
      if (selectedChapter) url += `&chapter=${selectedChapter}`;
      if (selectedTopic) url += `&topic=${selectedTopic}`;
      if (uncheckedOnly) url += `&unchecked_only=true`;

      const data = await apiClient.get(url);
      const questionsData = Array.isArray(data) ? data : (data?.results || []);
      
      if (Array.isArray(questionsData) && questionsData.length > 0) {
        setQuestions(questionsData);
        setCurrentIndex(0);
      } else {
        setError('No questions found with the selected filters.');
      }
    } catch (err) {
      console.error('Filter search error:', err);
      setError(`Failed to search for questions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkChecked = async () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return;

    setUpdating(true);
    try {
      const data = await apiClient.patch(`/api/courses/questions/${currentQuestion.id}/`, {
        is_checked: !currentQuestion.is_checked
      });
      
      // Update the question in the list
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = data;
      setQuestions(updatedQuestions);
    } catch (err) {
      console.error('Failed to update checked status:', err);
      setError('Failed to update checked status.');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateQuestion = async () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return;

    setUpdating(true);
    try {
      // Build update payload
      const payload = {
        chapter_id: editChapter || null,
        topic_id: editTopic || null,
        question_text: editQuestionText,
        explanation: editExplanation,
        answers: editAnswers.map(a => ({ id: a.id, answer_text: a.answer_text, is_correct: a.is_correct }))
      };

      const data = await apiClient.patch(`/api/courses/questions/${currentQuestion.id}/`, payload);
      
      // Update the question in the list
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = data;
      setQuestions(updatedQuestions);
      
      // Mark all modified fields as saved
      const newAnswerStatuses = {};
      data.answers?.forEach(a => {
        newAnswerStatuses[a.id] = fieldStatus.answers[a.id] === 'modified' ? 'saved' : fieldStatus.answers[a.id];
      });
      setFieldStatus(prev => ({
        questionText: prev.questionText === 'modified' ? 'saved' : prev.questionText,
        chapter: prev.chapter === 'modified' ? 'saved' : prev.chapter,
        topic: prev.topic === 'modified' ? 'saved' : prev.topic,
        explanation: prev.explanation === 'modified' ? 'saved' : prev.explanation,
        answers: newAnswerStatuses
      }));
      
      setError(null);
    } catch (err) {
      console.error('Failed to update question:', err);
      setError('Failed to update question.');
    } finally {
      setUpdating(false);
    }
  };

  // Handle question text change
  const handleQuestionTextChange = (value) => {
    setEditQuestionText(value);
    const currentQuestion = questions[currentIndex];
    const isModified = value !== currentQuestion?.question_text;
    setFieldStatus(prev => ({
      ...prev,
      questionText: isModified ? 'modified' : 'unchanged'
    }));
  };

  // Handle answer text change
  const handleAnswerTextChange = (answerId, value) => {
    setEditAnswers(prev => prev.map(a => 
      a.id === answerId ? { ...a, answer_text: value } : a
    ));
    const currentQuestion = questions[currentIndex];
    const originalAnswer = currentQuestion?.answers?.find(a => a.id === answerId);
    const isModified = value !== originalAnswer?.answer_text;
    setFieldStatus(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [answerId]: isModified ? 'modified' : 'unchanged'
      }
    }));
  };

  // Handle explanation change
  const handleExplanationChange = (value) => {
    setEditExplanation(value);
    const currentQuestion = questions[currentIndex];
    const isModified = value !== (currentQuestion?.explanation || '');
    setFieldStatus(prev => ({
      ...prev,
      explanation: isModified ? 'modified' : 'unchanged'
    }));
  };

  // Handle chapter change
  const handleChapterChange = (value) => {
    setEditChapter(value);
    setEditTopic('');
    const currentQuestion = questions[currentIndex];
    const isModified = value !== (currentQuestion?.chapter_id || '');
    setFieldStatus(prev => ({
      ...prev,
      chapter: isModified ? 'modified' : 'unchanged',
      topic: 'modified' // Topic will change when chapter changes
    }));
  };

  // Handle topic change
  const handleTopicChange = (value) => {
    setEditTopic(value);
    const currentQuestion = questions[currentIndex];
    const isModified = value !== (currentQuestion?.topic_id || '');
    setFieldStatus(prev => ({
      ...prev,
      topic: isModified ? 'modified' : 'unchanged'
    }));
  };

  // Handle correct answer change
  const handleCorrectAnswerChange = (answerId) => {
    setEditAnswers(prev => prev.map(a => ({
      ...a,
      is_correct: a.id === answerId
    })));
    // Mark all answers as modified since we changed which one is correct
    setFieldStatus(prev => {
      const newAnswerStatuses = {};
      editAnswers.forEach(a => {
        newAnswerStatuses[a.id] = 'modified';
      });
      return {
        ...prev,
        answers: newAnswerStatuses
      };
    });
  };

  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImageFile(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion || !selectedImageFile) return;

    setUploadingImage(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('annex_image', selectedImageFile);

      const token = localStorage.getItem('atpl_access_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/courses/questions/${currentQuestion.id}/upload_image/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = data;
      setQuestions(updatedQuestions);
      setSelectedImageFile(null);
    } catch (err) {
      console.error('Failed to upload image:', err);
      setError(`Failed to upload image: ${err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion || !currentQuestion.annex_image) return;
    if (!window.confirm('Remove this annex image?')) return;

    setUploadingImage(true);
    setError(null);
    try {
      const token = localStorage.getItem('atpl_access_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/courses/questions/${currentQuestion.id}/upload_image/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = data;
      setQuestions(updatedQuestions);
    } catch (err) {
      console.error('Failed to remove image:', err);
      setError(`Failed to remove image: ${err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const currentQuestion = questions[currentIndex];

  return (
    <div className="h-screen bg-gray-100 dark:bg-gray-900 p-2 overflow-hidden">
      <div className="max-w-full mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="mb-2 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Question Lookup
            </h1>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Search by question number or filter by subject, chapter, and topic
            </p>
          </div>
          <button
            onClick={() => setShowCreateQuestion(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Question
          </button>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-3 flex-1 min-h-0">
          {/* Left Navigation Panel */}
          {questions.length > 0 && (
            <div className="w-64 flex-shrink-0">
              <div className="bg-white dark:bg-gray-800 rounded shadow h-full flex flex-col">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-white">
                    Questions ({questions.length})
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {questions.map((question, index) => (
                    <div
                      key={question.id}
                      onClick={() => setCurrentIndex(index)}
                      className={`p-2 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${
                        index === currentIndex
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-xs font-medium ${
                          index === currentIndex
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {question.question_number}
                        </span>
                        {question.is_checked && (
                          <CheckIcon className="w-3 h-3 text-green-600" />
                        )}
                      </div>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-1">
                        {question.question_text}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {question.chapter_name && (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                            {question.chapter_name}
                          </span>
                        )}
                        {question.topic_name && (
                          <span className="inline-block px-1.5 py-0.5 text-[9px] rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                            {question.topic_name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded shadow p-3 mb-2 flex-shrink-0">
          {/* Search by Question Number */}
          <div className="mb-3">
            <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Search by Question Number</h3>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter question number (e.g., DLR00123)"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !searchQuery.trim()}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Search
              </button>
            </form>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Or Filter by Category</h3>
            
            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
              {/* Subject */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Subject</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                  ))}
                </select>
              </div>

              {/* Chapter */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Chapter</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  disabled={!selectedSubject}
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">All Chapters</option>
                  {chapters.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Topic</label>
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  disabled={!selectedChapter}
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">All Topics</option>
                  {topics.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Unchecked Only + Search Button */}
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uncheckedOnly}
                    onChange={(e) => setUncheckedOnly(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Unchecked only</span>
                </label>
                <button
                  onClick={handleFilterSearch}
                  disabled={loading || !selectedSubject}
                  className="px-3 py-1 text-sm bg-green-600 text-white font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Display
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Question Display */}
        {currentQuestion && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded shadow flex-1 flex flex-col overflow-hidden">
            {/* Question Header */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Question {currentIndex + 1} of {questions.length}
                    </span>
                    {currentQuestion.is_checked && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        <CheckIcon className="w-2.5 h-2.5 mr-0.5" />
                        Checked
                      </span>
                    )}
                  </div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">
                    {currentQuestion.question_number}
                  </h2>
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {currentQuestion.subject_name || 'Unknown Subject'}
                </span>
                {currentQuestion.chapter_name && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                    {currentQuestion.chapter_name}
                  </span>
                )}
                {currentQuestion.topic_name && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                    {currentQuestion.topic_name}
                  </span>
                )}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  currentQuestion.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                  currentQuestion.difficulty === 'hard' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' :
                  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                }`}>
                  {currentQuestion.difficulty}
                </span>
              </div>
            </div>

            {/* Question Content */}
            <div className="p-3 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Question Text and Options */}
                <div className="space-y-4">
                  {/* Editable Question Text */}
                  <div className="mb-2">
                    <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Question Text</label>
                    <textarea
                      value={editQuestionText}
                      onChange={(e) => handleQuestionTextChange(e.target.value)}
                      rows={3}
                      className={`w-full px-2 py-1.5 text-sm border-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${getBorderClass(fieldStatus.questionText)}`}
                    />
                  </div>

                  {/* Answer Options */}
                  {currentQuestion.question_type === 'USER_INPUT' ? (
                    <div className="space-y-2">
                      <div className="p-2 border-2 border-gray-200 dark:border-gray-600 rounded">
                        <span className="text-xs text-gray-500 dark:text-gray-400">User Input Question</span>
                      </div>
                      {currentQuestion.correct_answer_text && (
                        <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                          <span className="text-xs font-medium text-green-800 dark:text-green-400">
                            Correct Answer: {currentQuestion.correct_answer_text}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Answer Options (select correct answer)</label>
                      {editAnswers.map((answer, index) => {
                        const isCorrect = answer.is_correct;
                        const answerStatus = fieldStatus.answers[answer.id] || 'unchanged';
                        
                        return (
                          <div key={answer.id} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="correct_answer"
                              checked={isCorrect}
                              onChange={() => handleCorrectAnswerChange(answer.id)}
                              className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                            />
                            <span className={`text-xs font-semibold min-w-[20px] ${isCorrect ? 'text-green-600' : 'text-gray-700 dark:text-gray-300'}`}>
                              {String.fromCharCode(65 + index)}.
                            </span>
                            <div className="flex-1 relative">
                              <input
                                type="text"
                                value={answer.answer_text}
                                onChange={(e) => handleAnswerTextChange(answer.id, e.target.value)}
                                className={`w-full px-2 py-1.5 text-sm border-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${getBorderClass(answerStatus)} ${isCorrect ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                              />
                              {isCorrect && (
                                <CheckIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-600" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Editable Explanation */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400">Explanation</label>
                      <button
                        onClick={() => setExplanationPreviewMode(!explanationPreviewMode)}
                        className="text-[9px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {explanationPreviewMode ? 'Edit' : 'Preview'}
                      </button>
                    </div>
                    {explanationPreviewMode ? (
                      <div className={`w-full px-2 py-1.5 text-sm border-2 rounded bg-gray-50 dark:bg-gray-800 min-h-[60px] ${getBorderClass(fieldStatus.explanation)}`}>
                        {editExplanation ? (
                          <MathText 
                            text={editExplanation}
                            className="text-gray-900 dark:text-white"
                          />
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">No explanation provided</span>
                        )}
                      </div>
                    ) : (
                      <textarea
                        value={editExplanation}
                        onChange={(e) => handleExplanationChange(e.target.value)}
                        rows={2}
                        placeholder="Add explanation for this question... (Use $formula$ for inline math, $$formula$$ for block math)"
                        className={`w-full px-2 py-1.5 text-sm border-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${getBorderClass(fieldStatus.explanation)}`}
                      />
                    )}
                  </div>
                </div>

                {/* Annex Image */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400">Annex Image</label>
                  {currentQuestion.annex_image ? (
                    <div className="flex justify-center">
                      <img
                        src={getImageUrl(currentQuestion.annex_image)}
                        alt="Question annex"
                        className="max-w-full max-h-80 object-contain rounded-lg cursor-pointer border border-gray-200 dark:border-gray-600 hover:border-blue-400 transition-colors"
                        onClick={() => setShowImageModal(true)}
                      />
                    </div>
                  ) : (
                    <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center text-xs text-gray-500 dark:text-gray-400">
                      No annex image
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded cursor-pointer transition-colors ${
                      uploadingImage ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}>
                      <PhotoIcon className="w-4 h-4" />
                      {selectedImageFile ? selectedImageFile.name.length > 20 ? `${selectedImageFile.name.slice(0, 17)}...` : selectedImageFile.name : 'Choose Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                    </label>
                    {selectedImageFile && (
                      <button
                        onClick={handleImageUpload}
                        disabled={uploadingImage}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {uploadingImage ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <PhotoIcon className="w-4 h-4" />
                            Upload
                          </>
                        )}
                      </button>
                    )}
                    {currentQuestion.annex_image && (
                      <button
                        onClick={handleImageDelete}
                        disabled={uploadingImage}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Chapter/Topic Section */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Chapter</label>
                  <select
                    value={editChapter}
                    onChange={(e) => handleChapterChange(e.target.value)}
                    className={`w-full px-3 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm transition-colors ${getBorderClass(fieldStatus.chapter)}`}
                  >
                    <option value="">No Chapter</option>
                    {editChapters.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Topic</label>
                  <select
                    value={editTopic}
                    onChange={(e) => handleTopicChange(e.target.value)}
                    disabled={!editChapter}
                    className={`w-full px-3 py-2 border-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50 transition-colors ${getBorderClass(fieldStatus.topic)}`}
                  >
                    <option value="">No Topic</option>
                    {editTopics.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleUpdateQuestion}
                  disabled={updating}
                  className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {updating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Similar Questions Concept Section */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Similar Questions Concepts
              </h3>
              
              {/* Current concepts */}
              {questionConcepts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {questionConcepts.map(c => (
                    <span key={c.concept_question_id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full">
                      {c.identifier}: {c.title}
                      <button
                        onClick={() => handleRemoveConcept(c.concept_question_id)}
                        className="ml-1 hover:text-red-600"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Assign to concept */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <select
                    value={selectedConcept}
                    onChange={(e) => setSelectedConcept(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Select a concept...</option>
                    {concepts.map(c => (
                      <option key={c.id} value={c.id}>{c.identifier}: {c.title}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleAssignConcept}
                  disabled={!selectedConcept}
                  className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  Assign
                </button>
                <button
                  onClick={() => setShowCreateConcept(true)}
                  className="px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                >
                  <PlusIcon className="w-4 h-4" />
                  New
                </button>
              </div>

              {/* Create concept form */}
              {showCreateConcept && (
                <div className="mt-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Identifier (e.g., NAV-001)"
                      value={newConceptIdentifier}
                      onChange={(e) => setNewConceptIdentifier(e.target.value)}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Title"
                      value={newConceptTitle}
                      onChange={(e) => setNewConceptTitle(e.target.value)}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <textarea
                    placeholder="Description (optional)"
                    value={newConceptDescription}
                    onChange={(e) => setNewConceptDescription(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateConcept}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => setShowCreateConcept(false)}
                      className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                Previous
              </button>

              <button
                onClick={handleMarkChecked}
                disabled={updating}
                className={`px-6 py-2 font-medium rounded-lg transition-colors ${
                  currentQuestion.is_checked
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {currentQuestion.is_checked ? 'Mark Unchecked' : 'Mark Checked'}
              </button>

              <button
                onClick={goToNext}
                disabled={currentIndex === questions.length - 1}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Image Modal */}
        {showImageModal && currentQuestion?.annex_image && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImageModal(false)}
          >
            <img
              src={getImageUrl(currentQuestion.annex_image)}
              alt="Question annex"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        )}

        {/* Create Question Modal */}
        {showCreateQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Question</h2>
                <button onClick={() => setShowCreateQuestion(false)} className="text-gray-500 hover:text-gray-700">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject *</label>
                  <select
                    value={newQuestion.subject_id}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, subject_id: e.target.value, chapter_id: '', topic_id: '' }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                    ))}
                  </select>
                </div>

                {/* Question Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Question Number</label>
                  <input
                    type="text"
                    value={newQuestion.question_number}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, question_number: e.target.value }))}
                    placeholder="e.g., Q001"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Question Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Question Text *</label>
                  <textarea
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, question_text: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Question Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Question Type</label>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, question_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                    <option value="USER_INPUT">User Input</option>
                  </select>
                </div>

                {/* Answers for Multiple Choice */}
                {newQuestion.question_type === 'MULTIPLE_CHOICE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Answer Options</label>
                    <div className="space-y-2">
                      {newQuestion.answers.map((ans, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="correct_answer"
                            checked={ans.is_correct}
                            onChange={() => setNewQuestion(prev => ({
                              ...prev,
                              answers: prev.answers.map((a, i) => ({ ...a, is_correct: i === idx }))
                            }))}
                            className="text-green-600"
                          />
                          <span className="font-medium text-gray-700 dark:text-gray-300 w-6">{String.fromCharCode(65 + idx)}.</span>
                          <input
                            type="text"
                            value={ans.answer_text}
                            onChange={(e) => setNewQuestion(prev => ({
                              ...prev,
                              answers: prev.answers.map((a, i) => i === idx ? { ...a, answer_text: e.target.value } : a)
                            }))}
                            placeholder={`Answer ${String.fromCharCode(65 + idx)}`}
                            className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Select the radio button for the correct answer</p>
                  </div>
                )}

                {/* Correct Answer for User Input */}
                {newQuestion.question_type === 'USER_INPUT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correct Answer</label>
                    <input
                      type="text"
                      value={newQuestion.correct_answer_text}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer_text: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Explanation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Explanation</label>
                  <textarea
                    value={newQuestion.explanation}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, explanation: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateQuestion(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateQuestion}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
                >
                  Create Question
                </button>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionLookup;
